// frontend/src/admin/v2/CronFailures.jsx
import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Alert, Box, Button, Collapse, FormControlLabel, IconButton, Skeleton, Stack,
  Switch, Table, TableBody, TableCell, TableHead, TableRow, Typography,
  useMediaQuery, useTheme,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { toast } from 'sonner'
import Panel from '../../components/common/Panel'
import ListToolbar from '../../components/common/ListToolbar'
import TableViewport from '../../components/common/TableViewport'
import TableFooterSimple from '../../components/common/TableFooterSimple'
import MobileRecordCard from '../../components/common/MobileRecordCard'
import RowActionsMenu from '../../components/common/RowActionsMenu'
import EmptyState from '../../components/common/EmptyState'
import Pill from '../../components/common/Pill'
import useContainerWidth from '../../components/common/useContainerWidth'
import useColumnPriority from '../../components/common/useColumnPriority'
import useListQuery from '../../components/common/listQuery/useListQuery'
import { COLUMN_MAX, CONTENT_WIDTH, FS, SP, V2 } from '../../theme/tokens'
import { formatBerlinDateTime } from '../../utils/time'
// 🔴 `formatQty`, nicht `formatNumber`: eine ANZAHL hat keine Nachkommastellen.
// `formatNumber` bringt zwei (`2,00`), und der Schalter las sich „(1.102,00)".
import { formatQty } from '../../utils/format'

const API = process.env.REACT_APP_API_BASE_URL

/** 50 je Seite ist die Hausgroesse (§9.6) — die Seite blaettert am SERVER. */
const ROWS_PER_PAGE = 50

/** Die acht Kopfzellen brechen nicht um (Hausform `pages/KnShipments.jsx`). */
const KOPF_SX = { whiteSpace: 'nowrap' }

/**
 * Die Spalten der Tabelle — Deskriptoren fuer `useColumnPriority`.
 *
 * 🔴 **`Status` und `Actions` sind `kern` und duerfen nie fallen** (§11.1:
 * waagerechtes Rollen ist keine Form fuer eine Liste mit Zeilenaktionen; P19
 * verlangt die Aktion in der letzten Spalte bei JEDER Breite).
 *
 * 🔴 **R16-B1 (Nachbesserung 2, 2026-11-01): `Outcome` steht VOR `Class`.**
 * Vorher war es umgekehrt (`Class` `lg`, `Outcome` `xl`). Der Hook nimmt die
 * optionale Spalte aber in RANGFOLGE auf, solange das Budget unter der
 * Behaelterbreite liegt (`useColumnPriority.js:134-150`) — `lg` kam damit
 * zuerst und blieb laenger stehen. Auf dem Desktop (Behaelter ≈1050–1104 px)
 * passte genau eine optionale Spalte, und das war `Class`: die Spalte, die
 * heute in JEDER Zeile leer ist (M3: der Klassifizierer ist aus), waehrend
 * `Outcome` mit dem Exit-Code unsichtbar blieb — die Ansicht zeigte auf dem
 * groesseren Schirm WENIGER.
 *
 * Budget aus `COLUMN_MAX` (`tokens.js`): `kern` = 160 + 160 + 360 + 200 + 160
 * = **1040**, + `Outcome` (chip max 200) = **1240**, + `Class` = **1440**.
 * Also: `Outcome` ab > 1040 px Behaelterbreite, `Class` erst ab > 1240 px.
 */
const SPALTEN = [
  { key: 'aufklapper', priority: 'kern', kind: 'number' },
  { key: 'when', priority: 'kern', kind: 'date' },
  { key: 'job', priority: 'kern', kind: 'identity' },
  { key: 'outcome', priority: 'lg', kind: 'chip' },
  { key: 'class', priority: 'xl', kind: 'chip' },
  { key: 'status', priority: 'kern', kind: 'chip' },
  { key: 'actions', priority: 'kern', kind: 'number' },
]

/**
 * Feste Breiten der Kernspalten — die Tabelle springt nicht zwischen den Laegen.
 *
 * 🔴 **Nachbesserung 1 (erster Browserlauf, F1/F2):** die Zahlen sind an der
 * gerenderten Tabelle nachgezogen, nicht geschaetzt.
 *
 * | Spalte | vorher | jetzt | warum |
 * |---|---|---|---|
 * | `when` | 150 | **176** | `30.10.2026, 04:00` ist 17 Zeichen Monospace bei `FS.xs` (11,5 px) ≈ 118 px + 32 px Zellenrand = 150 — auf den Pixel genau, also brach das Datum in zwei Zeilen um (F2) |
 * | `class` | — (flexibel) | **140** | die Spalte sog den gesamten Rest der Tabelle auf und stand dabei leer da (M3: die Klasse ist NULL, solange `CRON_CLASSIFIER_ENABLED` aus ist) — der Platz fehlte `Job` (F2) |
 * | `actions` | 56 | **100** | „ACTIONS" (7 Zeichen, 0.7 rem, Großbuchstaben, `letter-spacing 0.06em`) braucht ≈ 90 px; bei 56 px schnitt der Kopf zu „ACTIO" ab (F1) |
 *
 * 🔴 **R16-W5 (Nachbesserung 2): DIESE Tabelle ist die EINE Quelle für die
 * festen Breiten.** Daneben rechnete `spaltenMinSumme` die Mindestbreite der
 * Tabelle aus `COLUMN_MAX[kind].min` (72/96/200/88/160) — zwei Zahlenreihen
 * für dieselben Spalten, die nicht übereinstimmten (Aufklapper 40 gegen 72,
 * Class 140 gegen 88). `COLUMN_MAX` bleibt, was der Hook verlangt: das
 * **Budget** (`max`) für die Sichtbarkeitsentscheidung und die **Untergrenze**
 * (`min`) für die Spalten OHNE feste Breite (`job`). Für alle anderen gilt die
 * Zahl aus dieser Tabelle (`spaltenMindestbreite`).
 */
const COLUMN_PX = {
  aufklapper: 40,
  // 🔴 R16-W-B (Nachbesserung 6, 2026-11-01): `outcome` stand hier nicht — `Job`
  // und `Outcome` teilten als einzige zwei Spalten ohne Breite den Rest GLEICH,
  // bei 1440 war `Outcome` ≈320 px und `Job` ≈295 px, obwohl `Job` zwei Zeilen
  // traegt und `Outcome` sechs Zeichen. 136 statt der vorgeschlagenen 120: die
  // Pille „No exit code" (12 Zeichen, `FS.sm` 12,5 px ≈ 71 px Text + 20 px
  // Polster + 2 px Rahmen ≈ 93 px) braucht in einer `size="small"`-Zelle
  // (16 px Rand je Seite) ≈125 px; bei 120 blieben 88 px und der Text braeche
  // um. `COLUMN_MAX.chip.max` (200) bleibt die Obergrenze.
  outcome: 136,
  // 🔴 R16-Rest (Nachbesserung 6): `when` liegt mit 176 ueber
  // `COLUMN_MAX.date.max` (160) und das ist Absicht — das Budget entscheidet die
  // SICHTBARKEIT (160), die Zelle traegt aber `30.10.2026, 04:00` (17 Zeichen
  // Monospace) und brach bei 160 um. Die 16 px sind Polster fuer den Wert, nicht
  // fuer die Entscheidung; `spaltenMindestbreite` liest darum zuerst diese
  // Tabelle.
  when: 176,
  class: 140,
  status: 110,
  actions: 100,
}

const STRICH = '—'

/** Die Erklaerung der leeren `Class`-Zelle — nur, wenn die Klasse WIRKLICH fehlt. */
const KLASSEN_LEGENDE =
  'Not classified — the classifier is off (CRON_CLASSIFIER_ENABLED).'

function spaltenBreite(key) {
  const px = COLUMN_PX[key]
  return px ? { width: px, minWidth: px } : {}
}

/**
 * Die Mindestbreite einer Spalte für `minWidth` der Tabelle.
 *
 * 🔴 **R16-W5:** die FESTE Breite, wo es eine gibt — sonst die Untergrenze
 * ihrer Art aus `COLUMN_MAX`. Vorher las diese Summe ausschliesslich
 * `COLUMN_MAX[kind].min` und widersprach damit `COLUMN_PX` für dieselben
 * Spalten (`useColumnPriority.js:173-179`: „ONE source for the one number").
 */
function spaltenMindestbreite(sp) {
  return COLUMN_PX[sp.key] ?? COLUMN_MAX[sp.kind]?.min ?? 0
}

/**
 * Text und Ton der `Outcome`-Pille — EINE Quelle fuer Tabelle und Karte.
 *
 * 🔴 **R16-W-A (Nachbesserung 6, 2026-11-01):** `exit_code` ist optional
 * (`backend/notify/cron_capture.py:74` setzt `exit_code=None`, die Route reicht
 * ihn roh durch). Bis hierher stand `${failure.exit_code}` an beiden Stellen —
 * ein rohes JS-`null` als Wert („Exit null") auf sechs von sieben echten
 * Zeilen. Unbekannt ist kein Wert, sondern eine fehlende Angabe: eigener Text,
 * ruhiger Ton. `0` ist ein ECHTER Code, deshalb `== null` und nicht `!`.
 */
function outcomePille(failure) {
  if (failure.timed_out) return { text: 'Timeout', tone: 'err' }
  if (failure.exit_code == null) return { text: 'No exit code', tone: 'plain' }
  return { text: `Exit ${failure.exit_code}`, tone: 'warn' }
}

/**
 * CronFailures — /admin/cron-failures page.
 *
 * @description
 *   Captured-failure history for every cron job (legacy admin scheduler +
 *   Render cron), with the list anatomy of `tabellen-filter-suche.md` §2.1:
 *   the unresolved switch and the result counter in Z1, the active-filter chip
 *   in Z3, a paginated table on the desktop and reduced rows on the phone.
 *
 *   🔴 **The default is a NARROWING filter.** `unresolved` is a `switch` with
 *   `default: true`, so the base state shows open failures only and Z3 carries
 *   the chip „Unresolved · default" (N4.2/P28). The ✕ on that chip writes
 *   `?unresolved=0` — and because `engtEin` is `false` afterwards, no chip and
 *   no „Clear all" remain. The switch in Z1 is therefore the SECOND way back
 *   (B2): without it the default would be a one-way street.
 *
 *   🔴 **The request carries `unresolved` EXPLICITLY in both states** (B-neu-1).
 *   The hook omits a dimension while it sits on its default, and the server
 *   reads `request.args.get("unresolved", "false")` — reading the parameter off
 *   the URL would send nothing in the base state and show resolved rows under a
 *   chip that says „Unresolved".
 *
 *   🔴 **The switch count is a SECOND, cheap request** (`?unresolved=true&limit=1`)
 *   and reads its `total`: the `total` of the running query counts the current
 *   filter, which is 0 open rows when the switch is off. It is not a
 *   „total only" call — the endpoint runs its `COUNT` and a `SELECT … LIMIT 1`
 *   whose row is discarded.
 *
 * @useWhen
 *   - Triaging cron failures without leaving the app
 *   - Verifying a fix actually stopped the recurring failure
 *
 * @related
 *   - CronHealthboard — uptime view (different angle)
 *   - JobsList — Run-now controls (live cron management)
 */
export default function CronFailures() {
  const theme = useTheme()
  const istSchmal = useMediaQuery(theme.breakpoints.down('sm'))

  // ── Der Listen-Zustand lebt in der ADRESSE (§3.1) ─────────────────────────
  const query = useListQuery({
    dimensions: [{ key: 'unresolved', kind: 'switch', default: true, label: 'Unresolved' }],
    pageSize: ROWS_PER_PAGE,
  })
  const { state, set, clearAll, chips } = query
  const nurOffen = state.unresolved === true
  const seite = Math.max(1, Number(state.page) || 1)

  // ── Der Abruf ─────────────────────────────────────────────────────────────
  const [failures, setFailures] = useState([])
  const [gesamt, setGesamt] = useState(null)
  const [offenGesamt, setOffenGesamt] = useState(null)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)
  const [jeGeladen, setJeGeladen] = useState(false)
  const jeGeladenRef = useRef(false)
  const [versuch, setVersuch] = useState(0)

  useEffect(() => {
    const steuerung = new AbortController()
    let abgemeldet = false
    setLaedt(true)
    if (jeGeladenRef.current) setFehler(null)
    const kopf = { signal: steuerung.signal }
    Promise.all([
      axios.get(`${API}/api/admin/notify/cron-failures`, {
        ...kopf,
        params: {
          // 🔴 B-neu-1: aus `state.unresolved`, NICHT aus der URL — der Hook
          // laesst die Vorgabe weg, der Server liest dann `false`.
          unresolved: nurOffen ? 'true' : 'false',
          limit: ROWS_PER_PAGE,
          offset: (seite - 1) * ROWS_PER_PAGE,
        },
      }),
      // Der zweite, billige Leseaufruf fuer die Trefferzahl des Schalters.
      axios.get(`${API}/api/admin/notify/cron-failures`, {
        ...kopf,
        params: { unresolved: 'true', limit: 1 },
      }),
    ])
      .then(([liste, offen]) => {
        if (abgemeldet) return
        const zeilen = Array.isArray(liste.data?.failures) ? liste.data.failures : []
        const zahl = Number.isFinite(liste.data?.total) ? liste.data.total : null
        setFailures(zeilen)
        setGesamt(zahl)
        setOffenGesamt(Number.isFinite(offen.data?.total) ? offen.data.total : null)
        setFehler(null)
        setJeGeladen(true)
        jeGeladenRef.current = true
        // 🔴 Eine Seite hinter dem Ende wird auf die LETZTE geklemmt (N10.4):
        // die Ansicht schreibt die Adresse nicht um, sie zeigt den letzten
        // Stand. Ohne die Klemmung stuende „0 of 120" ueber einer leeren Seite.
        if (zahl != null && zahl > 0) {
          const letzte = Math.max(1, Math.ceil(zahl / ROWS_PER_PAGE))
          if (seite > letzte) set('page', letzte)
        }
      })
      .catch((e) => {
        if (abgemeldet || steuerung.signal.aborted) return
        setFehler(e?.response?.data?.error || e?.message || 'Unknown error')
      })
      .finally(() => { if (!abgemeldet) setLaedt(false) })
    return () => { abgemeldet = true; steuerung.abort() }
  }, [nurOffen, seite, versuch, set])

  const wiederholen = useCallback(() => setVersuch((v) => v + 1), [])

  // 🔴 Die Sperre des ERSTEN Aufbaus (N8.5): nur solange noch nie etwas geladen
  // wurde. Ein spaeter gescheitertes Auffrischen laesst die Zeilen stehen.
  const sperre = !jeGeladen && fehler != null

  /**
   * Der Leerzustand.
   *
   * 🔴 **Die Vorgabe ist ein Filter, also ist „nichts da" hier zweierlei:** unter
   * `unresolved=true` heisst die leere Liste „keine OFFENEN Fehlschlaege" — der
   * Weg heraus ist der Schalter bzw. „Clear all filters" und zeigt dann ALLE
   * Zeilen. Erst wenn auch die ungefilterte Sicht leer ist, gibt es wirklich
   * nichts (`no-data`). Die zwei Texte sind deshalb keine zwei Fassungen
   * desselben, sondern zwei verschiedene Lagen.
   */
  // 🔴 R15-W1 (Nachbesserung 2): ein GESCHEITERTES NACHLADEN bei leerer Liste
  // kennt den Grund NICHT. Bis dahin fiel dieser Zustand in `no-match`/`no-data`
  // — die Flaeche behauptete „nichts da", waehrend die Wahrheit unbekannt war,
  // und der Zaehler zeigte die Zahl des letzten Erfolgslaufs (eine 0 fuer
  // „unbekannt"). `EmptyState` kennt seit 2026-10-11 genau dafuer `unknown`.
  const leerGrund = sperre
    ? 'error'
    : jeGeladen && fehler != null && failures.length === 0
      ? 'unknown'
      : jeGeladen && failures.length === 0
        ? (nurOffen ? 'no-match' : 'no-data')
        : null

  // 🔴 Nachbesserung 3 (2026-11-01): EINE Quelle fuer „der Stand ist
  // unbekannt". Der Zaehler in Z1 und der Schalter tragen dasselbe Zeichen;
  // zwei Bedingungen nebeneinander liefen auseinander (Foto `390_e4_unknown`:
  // Zaehler „—", Schalter „Unresolved only (0)").
  const standUnbekannt = leerGrund === 'unknown'

  // ── Breite und Spaltenbudget ─────────────────────────────────────────────
  const tabelleRef = useRef(null)
  const { width: behaelterBreite } = useContainerWidth(tabelleRef)
  const messBasis = behaelterBreite ?? CONTENT_WIDTH.reading
  const { visible: sichtbareSpalten } = useColumnPriority({
    columns: SPALTEN,
    containerWidth: messBasis,
  })
  const sichtbar = new Set(sichtbareSpalten.map((s) => s.key))
  const spaltenMinSumme = sichtbareSpalten.reduce(
    (summe, sp) => summe + spaltenMindestbreite(sp),
    0,
  )

  const handleResolve = async (fid) => {
    try {
      await axios.post(`${API}/api/admin/notify/cron-failures/${fid}/resolve`)
      toast.success('Marked resolved')
      wiederholen()
    } catch (e) {
      toast.error(`Resolve failed: ${e?.response?.data?.error || e.message}`)
    }
  }

  // ── Der Schalter — der ZWEITE Weg zurueck (B2) ───────────────────────────
  const schalter = (
    <FormControlLabel
      control={(
        <Switch
          size="small"
          checked={nurOffen}
          // N8.5: der Filter ist gesperrt, solange der erste Aufbau gescheitert
          // ist — nach dem ersten Aufbau bleibt er bedienbar (N4.5).
          disabled={sperre}
          onChange={(e) => set('unresolved', e.target.checked)}
        />
      )}
      label={standUnbekannt
        ? `Unresolved only (${STRICH})`
        : offenGesamt == null
          ? 'Unresolved only'
          : `Unresolved only (${formatQty(offenGesamt)})`}
      sx={{ m: 0, whiteSpace: 'nowrap' }}
    />
  )

  const zeilenAktionen = (f) => {
    const offen = !f.resolved_at
    return [{
      key: 'resolve',
      label: offen ? 'Mark resolved' : 'Already resolved',
      disabled: !offen,
      onClick: offen ? () => handleResolve(f.id) : undefined,
    }]
  }

  return (
    <Stack spacing={SP.section}>
      {/* Ein gescheitertes NACH-Laden, waehrend Zeilen stehen: die einzige
          Meldung, die der Leerzustand nicht abdeckt. Sie steht AUSSERHALB von
          `liste-panel` — P8 zaehlt dort genau EINEN `role="status"`. */}
      {/* 🔴 R15-W1: der Alert erklaert, dass die ZEILEN DARUNTER alt sind. Ohne
          Zeilen erklaert er nichts mehr („the rows below" waere eine Behauptung
          ueber null Zeilen) — dann traegt der `unknown`-Leerzustand die Meldung
          allein. */}
      {fehler && !sperre && failures.length > 0 && (
        <Alert severity="error" variant="outlined">
          The list could not be refreshed: {fehler}. The rows below are the last
          successful load.
        </Alert>
      )}

      <Panel pad="none" data-testid="liste-panel">
        <Box sx={{ px: SP.card, pt: SP.card }}>
          <ListToolbar
            // 🔴 Der Schalter sitzt in Z1 — im Facetten-Slot, dem Kriterien-Slot
            // der Leiste (E4 hat keine Suche und kein Blatt, §2.4/§2.11). Unter
            // `sm` blendet `ListToolbar` Facetten aus (N11.8: dort stehen
            // Such-Pille und „Filters"); die Mobilform des Schalters steht
            // deshalb ueber den Karten, sonst waere der Rueckweg auf dem Telefon
            // wieder eine Einbahnstrasse.
            facets={[<Box key="unresolved">{schalter}</Box>]}
            // 🔴 R16-W4 (Nachbesserung 2): E4 traegt GENAU EINEN Filter-Chip.
            // Ohne das Opt-in rollt Z3 auf dem Telefon waagerecht, und der ✕
            // des Chips steht am Rand abgeschnitten (Foto `390_e4_viel`: nur
            // ueber „Clear all" oder den Schalter loesbar). Das Opt-in kostet
            // eine Zeile — bei fuenf Chips waere das der falsche Tausch, bei
            // einem ist es die vollstaendige Bedienung. Die Hausform bleibt
            // unveraendert (`ActiveFilterBar.jsx:312-319`, Vorgabe einzeilig).
            z3UmbruchMobil
            count={leerGrund === 'error' ? undefined : {
              noun: 'failures',
              // 🔴 R15-W1: im `unknown`-Zustand ist die Zahl UNBEKANNT — nicht
              // die des letzten Erfolgslaufs. `n`/`m` bleiben leer, `unknown`
              // laesst den Baustein „—" zeichnen (Regel 22 §3).
              n: standUnbekannt || !jeGeladen ? null : failures.length,
              m: standUnbekannt || !jeGeladen ? null : gesamt,
              unknown: standUnbekannt,
              loading: laedt,
            }}
            query={{ chips, clearAll }}
          >
            {query.ignoredValues.length > 0 && (
              <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.tight }}>
                {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
              </Alert>
            )}

            {laedt && !jeGeladen && (
              <Box data-testid="liste-skelett" sx={{ px: SP.card, py: SP.card }}>
                <Stack spacing={SP.tight}>
                  {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}
                </Stack>
              </Box>
            )}

            {istSchmal && !(laedt && !jeGeladen) && (
              <Box sx={{ px: SP.card, pt: SP.tight }}>{schalter}</Box>
            )}

            {/* 🔴 Die Legende steht NUR, wenn die Klasse wirklich fehlt (M3:
                heute alle NULL, weil `CRON_CLASSIFIER_ENABLED` aus ist). Eine
                Legende ueber einer gefuellten Spalte erklaerte nichts.
                🔴 F4 (erster Browserlauf): sie steht seit der Nachbesserung
                HINTER dem Schalter und DIREKT ueber Tabelle bzw. Karten. Sie
                erklaert die `Class`-Spalte — also muss sie das erklaeren, was
                darunter steht. Vorher stand sie mobil zwischen Z3 und dem
                Schalter, und der Schalter trennte sie von ihrem Gegenstand.
                🔴 Nachbesserung 3 (2026-11-01): sie steht nur noch, wenn die
                Spalte WIRKLICH dasteht — `sichtbar.has('class')` am Desktop,
                und auf der Kartenform nie (die Karte traegt seit R16-W2 keinen
                Strich mehr, den sie erklaeren koennte). Bisher erklaerte sie
                bei 1440 eine Spalte, die das Budget weggelassen hatte. */}
            {!leerGrund && !istSchmal && sichtbar.has('class') && failures.some((f) => !f.classification) && (
              <Box sx={{ px: SP.card, pt: SP.tight }}>
                <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                  {KLASSEN_LEGENDE}
                </Typography>
              </Box>
            )}

            {!istSchmal && !(laedt && !jeGeladen) && (
              <Box
                ref={tabelleRef}
                data-testid="liste-tabelle"
                aria-busy={laedt ? 'true' : 'false'}
                sx={{ px: SP.card, pt: SP.card }}
              >
                <TableViewport
                  footerPadPx={0}
                  priorities={SPALTEN}
                  containerWidth={behaelterBreite}
                  edgeHint
                >
                  <Table size="small" sx={{ tableLayout: 'fixed', minWidth: spaltenMinSumme }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('aufklapper') }} />
                        {/* 🔴 R16-W3 (Nachbesserung 2): die Zeitzone gehoert an
                            den Kopf. Der Wert kommt aus `formatBerlinDateTime`
                            (`utils/time.js`, `timeZone: BERLIN_TZ`), die
                            Cron-Zeiten selbst sind UTC — ohne die Klammer ist
                            die Spalte mehrdeutig. Die Basis schrieb
                            „When (LOCAL)"; „Berlin" ist die genauere Angabe und
                            bleibt innerhalb der 176 px (≈140 px mit Polster,
                            `COLUMN_MAX.date.max` = 160). */}
                        {sichtbar.has('when') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('when') }}>When (Berlin)</TableCell>}
                        {sichtbar.has('job') && <TableCell sx={KOPF_SX}>Job</TableCell>}
                        {sichtbar.has('outcome') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('outcome') }}>Outcome</TableCell>}
                        {sichtbar.has('class') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('class') }}>Class</TableCell>}
                        {sichtbar.has('status') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('status') }}>Status</TableCell>}
                        {sichtbar.has('actions') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('actions') }}>Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leerGrund ? (
                        <TableRow>
                          <TableCell colSpan={sichtbareSpalten.length}>
                            <Leerzustand
                              grund={leerGrund}
                              fehler={fehler}
                              onRetry={wiederholen}
                              onClearFilters={() => set('unresolved', false)}
                            />
                          </TableCell>
                        </TableRow>
                      ) : failures.map((f) => (
                        <FailureRow
                          key={f.id}
                          failure={f}
                          sichtbar={sichtbar}
                          spalten={sichtbareSpalten.length}
                          actions={zeilenAktionen(f)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableViewport>
              </Box>
            )}

            {istSchmal && !(laedt && !jeGeladen) && (
              <Box data-testid="liste-karte" sx={{ px: SP.card, pt: SP.card }}>
                {leerGrund ? (
                  <Leerzustand
                    grund={leerGrund}
                    fehler={fehler}
                    onRetry={wiederholen}
                    onClearFilters={() => set('unresolved', false)}
                  />
                ) : (
                  <Stack spacing={SP.tight}>
                    {failures.map((f) => (
                      <FailureCard key={f.id} failure={f} actions={zeilenAktionen(f)} />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            {/* Die Fusszeile steht NUR mit mehr als einer Seite (N8.7). */}
            {(gesamt ?? 0) > ROWS_PER_PAGE && !leerGrund && (
              <Box data-testid="liste-fuss">
                <TableFooterSimple
                  count={gesamt ?? 0}
                  page={seite - 1}
                  onPageChange={(p) => set('page', p + 1)}
                  rowsPerPage={ROWS_PER_PAGE}
                  // BS17 (das EINE Opt-in dieser Karte): mobil steht die Leiste
                  // im Fluss nach der letzten Karte.
                  stickyOnMobile={false}
                />
              </Box>
            )}
          </ListToolbar>
        </Box>
      </Panel>
    </Stack>
  )
}

/**
 * Die drei Leerzustaende an EINER Stelle — Tabelle und Karte teilen sie.
 *
 * `no-match` ist hier die VORGABE-Lage (offene Zeilen, keine da) und traegt
 * deshalb einen Weg heraus: „Clear all filters" schaltet den Schalter aus und
 * zeigt alle Zeilen. `no-data` hat keinen Filter zu raeumen.
 */
function Leerzustand({ grund, fehler, onRetry, onClearFilters }) {
  if (grund === 'error') {
    return (
      <EmptyState
        reason="error"
        message={`Cron failures could not be loaded: ${fehler}`}
        onRetry={onRetry}
        showDuringOutage
      />
    )
  }
  if (grund === 'no-match') {
    return (
      <EmptyState
        reason="no-match"
        message="No failures match the current filter."
        onClearFilters={onClearFilters}
      />
    )
  }
  // 🔴 R15-W1: der Grund ist UNBEKANNT — ein Nachladen ist gescheitert, waehrend
  // die Liste leer stand. Kein „nichts da" (das waere eine Behauptung) und
  // kein „nichts gefunden" (dafuer fehlt der Beleg). Der Weg heraus ist der
  // Wiederholen-Knopf.
  if (grund === 'unknown') {
    return (
      <EmptyState
        reason="unknown"
        message="The list could not be refreshed. Whether there are failures is unknown."
        detail={fehler ? `Last error: ${fehler}` : undefined}
        onRetry={onRetry}
      />
    )
  }
  return (
    <EmptyState
      reason="no-data"
      message="No cron failures recorded."
      detail="Failures appear here as soon as a cron run fails."
    />
  )
}

/** Der Aufklapper-Inhalt — Diagnose (LLM) und Stderr-Ausschnitt. */
function Diagnose({ failure }) {
  return (
    <Box sx={{ p: SP.card }}>
      {failure.root_cause_summary && (
        <Box sx={{ mb: SP.card }}>
          <Typography sx={{ fontSize: FS.xs, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
            DIAGNOSIS (LLM)
          </Typography>
          <Typography sx={{ fontSize: FS.sm }}>{failure.root_cause_summary}</Typography>
          {failure.suggested_next_action && (
            <Typography sx={{ fontSize: FS.sm, mt: 0.5 }}>
              <strong>Next:</strong> {failure.suggested_next_action}
            </Typography>
          )}
        </Box>
      )}
      <Typography sx={{ fontSize: FS.xs, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
        STDERR TAIL
      </Typography>
      <Box component="pre" sx={{
        fontSize: FS.xxs, fontFamily: 'SF Mono, Menlo, monospace',
        bgcolor: V2.ink, color: V2.wash, p: 1.5, borderRadius: 1,
        overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: 320, overflowY: 'auto',
      }}>
        {failure.stderr_tail || '(no stderr captured — set CRON_CAPTURE_STDERR=true)'}
      </Box>
    </Box>
  )
}

/** Der Zustand einer Zeile — Text UND Farbe, nie Farbe allein (P22). */
function StatusPille({ failure }) {
  return failure.resolved_at
    ? <Pill tone="ok">Resolved</Pill>
    : <Pill tone="warn">Unresolved</Pill>
}

function FailureRow({ failure, sichtbar, spalten, actions }) {
  const [offen, setOffen] = useState(false)
  const outcome = outcomePille(failure)
  return (
    <>
      <TableRow hover sx={{ '& td': { borderBottom: offen ? 0 : undefined } }}>
        <TableCell sx={spaltenBreite('aufklapper')}>
          <IconButton
            size="small"
            onClick={() => setOffen((v) => !v)}
            aria-expanded={offen}
            aria-label={`${offen ? 'Collapse' : 'Expand'} details for ${failure.cron_job_label}`}
          >
            {offen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        {sichtbar.has('when') && (
          // 🔴 F2: ein Datum bricht nicht um. `nowrap` ist die Zusicherung an der
          // Zelle selbst — die Breite allein haengt an der Schrift und an
          // `fontVariant` des Systems und war auf den Pixel genau.
          <TableCell sx={{
            fontFamily: 'SF Mono, Menlo, monospace',
            fontSize: FS.xs,
            whiteSpace: 'nowrap',
          }}>
            {formatBerlinDateTime(failure.occurred_at)}
          </TableCell>
        )}
        {sichtbar.has('job') && (
          <TableCell>
            <Typography sx={{ fontSize: FS.sm, fontWeight: 600 }}>
              {failure.cron_job_label}
            </Typography>
            {failure.script_path && (
              <Typography sx={{ fontSize: FS.xxs, color: 'text.secondary', fontFamily: 'SF Mono, Menlo, monospace' }}>
                {failure.script_path}
              </Typography>
            )}
          </TableCell>
        )}
        {sichtbar.has('outcome') && (
          <TableCell sx={spaltenBreite('outcome')}>
            <Pill tone={outcome.tone}>{outcome.text}</Pill>
          </TableCell>
        )}
        {sichtbar.has('class') && (
          <TableCell sx={spaltenBreite('class')}>
            {failure.classification
              ? <Pill>{failure.classification}</Pill>
              : <Typography sx={{ fontSize: FS.xs, color: 'text.disabled' }}>{STRICH}</Typography>}
          </TableCell>
        )}
        {sichtbar.has('status') && (
          <TableCell><StatusPille failure={failure} /></TableCell>
        )}
        {sichtbar.has('actions') && (
          <TableCell sx={spaltenBreite('actions')}>
            <RowActionsMenu
              label={`Row actions for ${failure.cron_job_label}`}
              actions={actions}
            />
          </TableCell>
        )}
      </TableRow>
      {offen && (
        <TableRow>
          <TableCell colSpan={spalten} sx={{ pt: 0, pb: SP.card, bgcolor: 'background.default' }}>
            <Collapse in={offen} timeout={150}>
              <Diagnose failure={failure} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

/** Die reduzierte Zeile (N11.7) — Titel, When · Outcome · Class, Status, ⋮. */
function FailureCard({ failure, actions }) {
  const [offen, setOffen] = useState(false)
  const outcome = outcomePille(failure)
  return (
    <Stack spacing={SP.chip}>
      <MobileRecordCard
        variant="row"
        // 🔴 G14a-2-Nachbesserung 1, F3: `karte-kennung-` sitzt am KARTENKASTEN,
        // nicht am Kennungs-Slot (Hausform `ThemenKarte`, `SourceManagerPage.jsx`
        // :693 — dort steht die Marke seit NB1 am Kasten). Am Slot war der Knoten
        // nur `#1`; P19 mobil sucht den ⋮ INNERHALB der Kennung und fand weder
        // Titel noch Menue („Keine Karte traegt einen ⋮", `hoechste: 0`).
        data-testid={`karte-kennung-${failure.id}`}
        title={(
          <Typography sx={{ fontSize: FS.sm, fontWeight: 600 }}>
            {failure.cron_job_label}
          </Typography>
        )}
        // 🔴 F3: die Kennung der Karte ist DIESELBE wie am Desktop. Dort steht in
        // der `Job`-Spalte der Job-Name (Titel) und darunter der Skriptpfad. Der
        // Pfad fehlte mobil ganz, und `#1` ist eine Datenbank-Nummer, die am
        // Desktop nirgends steht.
        // 🔴 R16-W1 (Nachbesserung 2): fehlt der Pfad, steht dort NICHTS — kein
        // `#<id>` als Rueckfall. Die echten Zeilen tragen keinen Pfad (die
        // sieben Fehlschlaege im Foto `1440_e4_echt` haben alle keinen), also
        // war der Rueckfall der NORMALFALL und zeigte wieder die Datenbank-
        // Nummer. `MobileRecordCard` rendert den Slot nur bei einem WERT
        // (`:364`), ein leeres `id` erzeugt also keine Geisterzeile.
        id={failure.script_path || undefined}
        meta={(
          <Stack spacing={SP.chip}>
            <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
              {formatBerlinDateTime(failure.occurred_at)}
            </Typography>
            <Stack direction="row" gap={SP.tight} flexWrap="wrap" alignItems="center">
              <Pill tone={outcome.tone}>{outcome.text}</Pill>
              {/* 🔴 R16-W2 (Nachbesserung 2): auf der Karte faellt der Strich
                  der leeren Klasse WEG. Die Karte traegt keine Spaltenueber-
                  schrift, die ihn als „Class" ausweist — er las sich als
                  Trennstrich zwischen „Exit 1" und „Details". Die Tabelle
                  behaelt ihn: dort steht `CLASS` darueber. */}
              {failure.classification && (
                <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                  {failure.classification}
                </Typography>
              )}
              <Button
                size="small"
                onClick={() => setOffen((v) => !v)}
                aria-expanded={offen}
              >
                {offen ? 'Hide details' : 'Details'}
              </Button>
            </Stack>
          </Stack>
        )}
        chips={<StatusPille failure={failure} />}
        actions={(
          <RowActionsMenu
            label={`Row actions for ${failure.cron_job_label}`}
            actions={actions}
          />
        )}
        fieldTestIds={{
          title: `karte-titel-${failure.id}`,
          // 🔴 `id` traegt KEINE Marke mehr: `karte-kennung-` sitzt am Kasten
          // (s. o.). Zwei Marken desselben Praefixes zaehlten die Karte doppelt.
          meta: `karte-meta-${failure.id}`,
          chips: `karte-status-${failure.id}`,
        }}
      />
      {offen && (
        <Collapse in={offen} timeout={150}>
          <Box sx={{ bgcolor: 'background.default' }}>
            <Diagnose failure={failure} />
          </Box>
        </Collapse>
      )}
    </Stack>
  )
}
